import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import Question from '../../components/Question/question'
import { questionMark } from '@app/assets/images'
import { APP_ROUTES, questions } from '@app/constants'
import { useNavigate } from 'react-router-dom'

interface QuestionnaireProps {
  translate: (key: string) => string
}

const Questionnaire: React.FC<QuestionnaireProps> = ({ translate }) => {
  const navigate = useNavigate()
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState<(string | null)[]>(Array(questions.length).fill(null))
  const totalSteps = questions.length

  const handleAnswer = (answer: string) => {
    setAnswers(prev => {
      const updated = [...prev]
      updated[step] = answer
      return updated
    })
  }

  const handleNext = () => {
    if (step < totalSteps - 1 && answers[step]) {
      setStep(step + 1)
    } else if (step === totalSteps - 1 && answers[step]) {
      navigate(APP_ROUTES.home)
    }
  }

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1)
    }
  }

  const progress = ((step + 1) / totalSteps) * 100

  return (
    <div className='min-h-screen flex flex-col bg-gray-50'>
      <div className='mt-6 mx-4 sm:mt-8 sm:mx-6'>
        <h2 className='text-xs sm:text-sm font-medium text-blue-500 mb-1 sm:mb-2 flex items-center space-x-1 sm:space-x-2'>
          <span>
            <img
              src={questionMark}
              alt='Question mark'
              className='h-3 w-3 sm:h-4 sm:w-4 mx-0.5 sm:mx-1 font-extrabold'
            />
          </span>
          {translate('risk_assessment.questionnaire.progress')
            .replace('{current}', String(step + 1))
            .replace('{total}', String(totalSteps))}
        </h2>
        <div className='w-full h-1.5 sm:h-2 bg-gray-200 rounded-full overflow-hidden'>
          <div
            style={{ width: `${progress}%` }}
            className='h-1.5 sm:h-2 bg-blue-600 rounded-full transition-all duration-500'
          />
        </div>
      </div>

      <header className='text-center mt-4 sm:mt-6'>
        <h1 className='text-xl sm:text-2xl font-semibold text-gray-800'>
          {translate('risk_assessment.questionnaire.header_title')}
        </h1>
      </header>

      <main className='flex-1 px-4 mt-4 sm:px-6 sm:mt-6 overflow-y-auto custom-scrollbar'>
        <div className='max-w-lg mx-auto'>
          <AnimatePresence mode='wait'>
            <motion.div
              key={step}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="min-h-full flex flex-col"
            >
              <Question
                question={questions[step].question}
                answers={questions[step].answers}
                onAnswer={handleAnswer}
                selected={answers[step] || null}
                translate={translate}
              />
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      <footer className='p-4 sm:p-6 flex-shrink-0'>
        <div className='flex space-x-3 sm:space-x-4'>
          <motion.button
            type='button'
            onClick={handleBack}
            initial={{ width: 0, opacity: 0, padding: 0 }}
            animate={
              step > 0
                ? { width: '30%', opacity: 1, padding: '0.625rem 0.75rem' }
                : { width: 0, opacity: 0, padding: 0 }
            }
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            aria-label={translate('risk_assessment.questionnaire.aria_back')}
            className='rounded-lg text-sm sm:text-base font-medium bg-gray-300 text-gray-700 hover:bg-gray-400 focus:ring-2 focus:ring-gray-400 focus:outline-none overflow-hidden'
          >
            {translate('risk_assessment.questionnaire.footer_back')}
          </motion.button>

          <motion.button
            type='button'
            onClick={handleNext}
            animate={{ flexBasis: step > 0 ? '70%' : '100%' }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            aria-label={
              step === totalSteps - 1
                ? translate('risk_assessment.questionnaire.aria_finish')
                : translate('risk_assessment.questionnaire.aria_next')
            }
            disabled={!answers[step]}
            className={`rounded-lg text-sm sm:text-base font-medium py-2.5 sm:py-3 transition-colors duration-200 focus:ring-2 focus:outline-none
              ${
                answers[step]
                  ? 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-400'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
          >
            {step === totalSteps - 1
              ? translate('risk_assessment.questionnaire.footer_finish')
              : translate('risk_assessment.questionnaire.footer_next')}
          </motion.button>
        </div>
      </footer>
    </div>
  )
}

export default Questionnaire
